import * as React from 'react';

import routes from 'defs/routes';
import Sequences from 'defs/sequences';

import { compileDynamicRoute } from 'helper/string';
import { OPTIONAL_ITEM_IDS } from 'store/optionalProgramItems';

import Dropdown, { OptionType } from 'components/atoms/Dropdown';
import IconInfo from 'components/atoms/IconInfo';
import Intensity from 'components/atoms/Intensity';
import Link from 'components/atoms/Link';
import Text from 'components/atoms/Text';
import Spacer from 'components/helper/Spacer';
import ButtonBar from 'components/molecules/ButtonBar';
import CardLike from 'components/molecules/Card/elements/Like';
import InputGroup from 'components/molecules/InputGroup';
import LinkList from 'components/molecules/LinkList';
import Measurement from 'components/molecules/Measurement';
import Overlay from 'components/molecules/Overlay';
import Rich from 'components/molecules/Rich';
import TableList from 'components/molecules/TableList';
import CardLayer from 'containers/molecules/CardLayer';

import withValidation from 'components/hoc/withValidation';
import CARD_RENDER_LOCATION from 'containers/molecules/Card/enum-card-render-location';
import { getMobileDevicePlatform, iOSScrollKeyboardPositionFix, PLATFORMS } from 'helper/device';
import i18n from 'helper/i18n';
import kcal from 'helper/kcal';
import sanitize from 'helper/sanitizer';
import { template } from 'helper/string';
import workoutDurationValidatorFactory from 'helper/validator/workout-duration';
import { delayed } from 'lib/Job';
import { actionCreators as exercisesActionCreators } from 'store/exercises';

import { LinkProps } from 'components/atoms/Link';
import { CardProps } from 'components/molecules/Card';
import { CardDetailType } from 'components/molecules/Card/Detail/CardDetailTypes';
import { TableListProps } from 'components/molecules/TableList';
import { ExerciseType } from 'store/exercises';

import { isString } from '@impuls-coach/util-is';
import { ButtonProps } from '../../Button/Button';

const ControlledInputDuration: any = withValidation(InputGroup, workoutDurationValidatorFactory, [
	sanitize.allowInteger,
	sanitize.maxLength(3),
]);

export type ToolType = {
	title?: string;
	href?: string;
	openInNewWindow?: boolean;
};

export type IosScrollFixType = {
	renderDetailSpacer: () => any;
};

export type CardDetailWorkoutDefaultProps = {
	title: string;
	externalId?: string;
	programItemExternalId: string;
	isDone: boolean;
	favorite: boolean;
	isCheckable: boolean;
	descriptionShort: string;
	description?: string;
	tableList: TableListProps;
	intensity: number;
	toolList: Array<ToolType>;
	energyKcal: number;
	repetitionsPerWorkoutSet: number;
	pausePerWorkoutSet: number;
	totalDuration: number;
	doSetProgramItemRef: (ref: number, externalId?: string) => void;
	programItemRef?: string;
	isSingleSequenceLoading: { [key: string]: boolean };
	isSequenceLoading: boolean;
	isDayInFuture: boolean;

	isCustom: boolean;
	weight: number;
	exercises: Array<ExerciseType>;
	selectedExercise?: ExerciseType;
	selectedCalories?: number | null;
	selectedDuration?: number | null;
	doRequestExercises?: typeof exercisesActionCreators.doRequestExercises;
	doPreselectExercise?: typeof exercisesActionCreators.doPreselectExercise;
	dayRef: number;
	parentProps: CardProps & any;
	doComplete: (programItemId: number) => void;
	doCreateItem: (programItemId: string, contentType: string, externalId: string, dayNumber: number) => void;
	doSwapItem: (contentType: string, externalId: string, lastDayRef: number) => void;
	doCreateCustomFromOptional: (
		contentType: string,
		programItemExternalId: string,
		exerciseExternalId: string,
		duration: number,
		met: number,
		dayNumber: number | string
	) => void;
	doMoveItem: (contentType: string, programItemExternalId: string, strategy: 'postpone', days?: number) => void;
	doWorkoutComplete(id: number, energyKcal: number): () => void;
	doReplaceWithCustomWorkout(
		programItemId: number,
		workoutExternalId: string,
		duration: number,
		met: number
	): () => void;
	doWorkoutCustomUpdate(programItemId: number, workoutExternalId: string, duration: number, met: number): () => void;
	doWorkoutCustomUpdateAndComplete(
		programItemId: number,
		workoutExternalId: string,
		duration: number,
		met: number
	): () => void;
};

export type CardDetailWorkoutProps = CardDetailType & CardDetailWorkoutDefaultProps;

export type CardDetailWorkoutStateTypes = {
	selectedExerciseId: number;
	isOverlayOpen: boolean;
	isOverlayOpened: boolean;
	hasValidDuration: boolean;
	hasValidExercise: boolean;
	dropdownValueExercise: {
		value: string;
		label: string;
	};
	inputValueDuration: string;
	isEditing: boolean;
};

export class CardDetailWorkout extends React.Component<CardDetailWorkoutProps, CardDetailWorkoutStateTypes> {
	get energyKcal(): number {
		return Math.round(this.props.energyKcal);
	}

	get isCompletedEditing(): boolean {
		if (!this.props.isDone) {
			return true;
		}
		return this.state.isEditing;
	}

	get isCompletedNotEditing(): boolean {
		if (!this.props.isDone) {
			return false;
		}
		return !this.state.isEditing;
	}

	get labelShowDetailButton(): string {
		return i18n(
			this.props.uiState === 'feed'
				? 'react.impulsCoach.components.cardDetail.workout.custom.feed.linkText'
				: 'react.impulsCoach.components.cardDetail.workout.custom.folded.linkText'
		);
	}

	get isIOS(): boolean {
		return getMobileDevicePlatform() === PLATFORMS.IOS;
	}
	public static displayName = 'CardDetailWorkout';

	/*
	 * FIXME: maybe this could be removed after iPhone 6 is not used anymore
	 * @see https:// jira.migros.net/browse/PULSWM-2992
	 * @see https:// jira.migros.net/browse/PULSWM-3117
	 */
	public iosScrollFix: IosScrollFixType = {
		renderDetailSpacer: () => {
			const { uiState } = this.props;
			if (!this.isIOS) {
				return null;
			}
			if (uiState !== 'detail') {
				return null;
			}
			return <div style={{ height: 200 }} />;
		},
	};
	constructor(props: CardDetailWorkoutProps) {
		super(props);

		const selectedExercise = props.selectedExercise || ({} as any);

		this.state = {
			selectedExerciseId: -1,
			isOverlayOpen: false,
			isOverlayOpened: false,
			hasValidExercise: Boolean(typeof selectedExercise.externalId === 'string'),
			hasValidDuration: Boolean(typeof props.selectedDuration === 'number'),
			dropdownValueExercise: {
				value: selectedExercise.externalId || '',
				label: selectedExercise.title || '',
			},
			inputValueDuration: (props.selectedDuration || '').toString(),
			isEditing: false,
		};
	}

	public componentDidMount() {
		if (this.props.doRequestExercises && this.props.isCustom) {
			this.props.doRequestExercises();
		}
	}

	public shouldComponentUpdate(nextProps: CardDetailWorkoutProps) {
		const { selectedExercise, selectedDuration } = this.props;

		if (this.props.doRequestExercises && nextProps.isCustom && !this.props.isCustom) {
			this.props.doRequestExercises();
		}

		if (
			(!selectedExercise && nextProps.selectedExercise) ||
			(selectedExercise &&
				nextProps.selectedExercise &&
				selectedExercise.title !== nextProps.selectedExercise.title &&
				selectedExercise.externalId !== nextProps.selectedExercise.externalId)
		) {
			this.setState({
				dropdownValueExercise: {
					value: (nextProps.selectedExercise || {}).externalId,
					label: (nextProps.selectedExercise || {}).title,
				},
				hasValidExercise: true,
			});
		}

		if (
			(!selectedExercise && nextProps.selectedExercise) ||
			(typeof selectedDuration === 'number' &&
				typeof nextProps.selectedDuration === 'number' &&
				selectedDuration !== nextProps.selectedDuration)
		) {
			this.setState({
				inputValueDuration: (nextProps.selectedDuration || '').toString(),
				hasValidDuration: true,
			});
		}

		return true;
	}

	public handleButtonClicks = (buttonType: string): void => {
		const { isSingleSequenceLoading, isCustom, isDone, energyKcal, history } = this.props;
		const { inputValueDuration } = this.state;

		const dayRef = this.props.dayRef;
		const programItemId = this.props.id;
		const returnToCertainDayURL = `${routes.today.indexRoute.href}/${this.props.parentProps.currentDayNumber ||
			''}`;

		switch (buttonType) {
			case 'completed':
				if (isSingleSequenceLoading[Sequences.CARD_IS_CHECKING]) {
					return;
				}

				if (isCustom) {
					const localExercise = this.getCurrentExercise();
					if (!localExercise) {
						return;
					}
					this.props.onComplete({
						type: 'workout',
						onAnimationDone: () => {
							if (isDone) {
								this.props.doWorkoutCustomUpdate(
									programItemId,
									localExercise.externalId,
									parseInt(inputValueDuration, 10),
									parseFloat(localExercise.met as any)
								);
							} else {
								const durationTransformed = parseInt(inputValueDuration, 10);
								const metTransformed = parseFloat(localExercise.met as any);

								this.props.doWorkoutCustomUpdateAndComplete(
									programItemId,
									localExercise.externalId,
									durationTransformed,
									metTransformed
								);
							}
						},
					});
				} else {
					this.props.onComplete({
						type: 'workout',
						onAnimationDone: () => {
							this.props.doWorkoutComplete(programItemId, energyKcal);
						},
					});
				}
				break;
			case 'pool':
				const { doSetProgramItemRef } = this.props;
				const targetPoolURL = compileDynamicRoute(i18n('react.impulsCoach.routes.pool.cluster.href'), {
					selectedPool: i18n('react.impulsCoach.routes.pool.selectedPool.workout.href'),
					cluster: '',
				});

				doSetProgramItemRef(programItemId, this.props.externalId);
				history.push(targetPoolURL);
				break;
			case 'select':
				const { doSwapItem, programItemRef, doCreateItem } = this.props;

				if (!programItemRef) {
					return;
				}

				if (!!~OPTIONAL_ITEM_IDS.indexOf(programItemRef)) {
					doCreateItem(`${programItemId}`, 'workout', 'ITOPTWORKOUT', dayRef);
					delayed(() => history.push(returnToCertainDayURL), 1000);
					return;
				}

				if (programItemId && programItemRef) {
					doSwapItem('workout', `${programItemId}`, this.props.parentProps.currentDayNumber);
					delayed(() => history.push(returnToCertainDayURL), 1000);
				}
				break;
			case 'select-custom-workout':
				const currentExercise = this.getCurrentExercise();
				const duration = parseInt(inputValueDuration, 10);
				const met = currentExercise ? parseFloat(currentExercise.met as any) : 0;

				if (!!~OPTIONAL_ITEM_IDS.indexOf(this.props.programItemRef as any)) {
					if (currentExercise) {
						this.props.doCreateCustomFromOptional(
							'workout', // content-type
							'ITOPTWORKOUT', // programItemExternalId
							currentExercise.externalId,
							duration,
							met,
							dayRef
						);
						delayed(() => history.push(returnToCertainDayURL), 1000);
					}
					return;
				}

				if (currentExercise) {
					this.props.doReplaceWithCustomWorkout(programItemId, currentExercise.externalId, duration, met);
					delayed(() => history.push(returnToCertainDayURL), 1000);
				}
				break;
			case 'postpone-later':
				this.props.doMoveItem('workout', `${this.props.id}`, 'postpone', 1);
				break;
			default:
				break;
		}
	};

	public handleTableListClick = (exerciseId: number) => {
		this.setState({
			selectedExerciseId: exerciseId,
			isOverlayOpen: true,
			isOverlayOpened: true,
		});
	};

	public handleOverlayClose = () => {
		this.setState({ isOverlayOpen: false, isOverlayOpened: false });
	};

	public getToolLinkListData = (): Array<LinkProps> => {
		const { toolList }: CardDetailWorkoutProps = this.props;
		const out: Array<LinkProps> = [];

		toolList.forEach(({ title, href, openInNewWindow }: ToolType, index: number) => {
			if (!isString(title) || !isString(href)) {
				return;
			}

			out.push({
				key: index,
				title,
				externalHref: href,
				icon: 'cart',
				openInNewWindow,
			} as any);
		});

		return out;
	};

	public renderButtonBar = () => {
		const {
			isDone,
			isCheckable,
			isSequenceLoading,
			isUiCompleting,
			uiState,
			cardRenderLocation,
			programItemRef,
			parentProps,
		} = this.props;

		let cardButtons: any = null;

		if (cardRenderLocation === CARD_RENDER_LOCATION.PROGRAM_FEED) {
			if (uiState === 'detail' && !isDone) {
				cardButtons = {
					buttons: [
						{
							loading: isUiCompleting,
							disabled: !isCheckable,
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'completed',
							},
						},
					],
				} as ButtonProps;
			} else if (uiState === 'detail' && this.props.state === 'folded' && this.props.isCustom) {
				cardButtons = {
					buttons: [
						{
							disabled: !isCheckable,
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'completed',
							},
						},
					],
				} as ButtonProps;
			} else if (this.props.state !== 'folded') {
				if (parentProps.isToday && !parentProps.isLastDay && !parentProps.disablePostponeButton) {
					cardButtons = {
						buttonsLayout: 'links',
						buttons: [
							{
								id: 'change-workout',
								modifier: '',
								link: {
									iconRight: '',
									iconLeft: 'change',
									title: i18n('react.impulsCoach.components.cardDetail.workout.poolText'),
									buttonBarId: 'pool',
								},
							},
							{
								id: 'postpone-later',
								modifier: '',
								link: {
									iconRight: '',
									iconLeft: 'close',
									title: i18n('react.impulsCoach.components.cardDetail.workout.postpone'),
									buttonBarId: 'postpone-later',
								},
							},
						],
					} as ButtonProps;
				} else {
					cardButtons = {
						buttons: [
							{
								id: 'change-workout',
								modifier: '',
								link: {
									iconRight: '',
									iconLeft: 'change',
									title: i18n('react.impulsCoach.components.cardDetail.workout.poolText'),
									buttonBarId: 'pool',
								},
							},
						],
					} as ButtonProps;
				}
			}
		}

		if (cardRenderLocation === CARD_RENDER_LOCATION.POOL && !!programItemRef) {
			cardButtons = {
				buttons: [
					{
						id: 'select-workout',
						link: {
							modifier: 'button',
							iconRight: '',
							iconLeft: '',
							title: i18n('react.impulsCoach.components.cardDetail.workout.selectReplaceText'),
							buttonBarId: 'select',
						},
					},
				],
			} as ButtonProps;
		}

		// Buttons for Custom Workout and user comes from a program item
		// - without a program item there is no button visible
		if (cardRenderLocation === CARD_RENDER_LOCATION.POOL_CUSTOM && !!programItemRef) {
			cardButtons = {
				buttons: [
					{
						id: 'select-custom-workout',
						disabled: !this.state.hasValidDuration || !this.state.hasValidExercise,
						link: {
							modifier: 'button',
							iconRight: '',
							iconLeft: '',
							title: i18n('react.impulsCoach.components.cardDetail.workout.selectReplaceText'),
							buttonBarId: 'select-custom-workout',
						},
					},
				],
			} as ButtonProps;
		}

		if (cardButtons === null) {
			return null;
		}

		return (
			<div className="m-card__button-bar">
				<ButtonBar handleButtonClicks={this.handleButtonClicks} disabled={isSequenceLoading} {...cardButtons} />
			</div>
		);
	};

	public renderRepetitionsPerWorkoutSet = (): any => {
		const { repetitionsPerWorkoutSet, pausePerWorkoutSet } = this.props;

		if (repetitionsPerWorkoutSet > 1) {
			const templateString = i18n('react.impulsCoach.components.cardDetail.workout.repetitionsPerWorkoutSetHTML');
			const contents = template(templateString, {
				amount: repetitionsPerWorkoutSet,
				pause: pausePerWorkoutSet,
			});

			return <Rich contents={contents} />;
		}
	};

	public getInputLabel(field: string, type: string): string {
		return i18n(`react.impulsCoach.components.cardDetail.workout.custom.field.${field}.${type}`);
	}

	public handleValidInputDuration = (error?: string): void => {
		this.setState({
			hasValidDuration: Boolean(error === null),
		});
	};

	public handleInputDuration = (inputValue: string | number): void => {
		this.setState({
			inputValueDuration: String(inputValue),
		});
	};

	public handleDropdownExercise = (inputValue: OptionType): void => {
		if (!this.props.doPreselectExercise) {
			return;
		}
		const doPreselectExercise = this.props.doPreselectExercise;
		this.setState(
			{
				dropdownValueExercise: {
					value: inputValue.value.toString(),
					label: inputValue.label,
				},
				hasValidExercise: true,
			},
			() => {
				doPreselectExercise(inputValue.value.toString(), this.props.id.toString());
			}
		);
	};

	public handleToggleEditing = (): void => {
		this.setState({
			isEditing: true,
		});
	};

	public calculateCalories = (met: number, kg: number, duration: number): number => {
		const result = met * kg * (duration / 60);
		if (isNaN(result)) {
			return 0;
		}
		return result;
	};

	public getCurrentExercise = (): ExerciseType | void => {
		return this.props.exercises.find((e) => e.externalId === this.state.dropdownValueExercise.value);
	};

	public renderCustomWorkoutDetail = () => {
		const {
			handleCardContentClick,
			handleShowDetailsClick,
			weight,
			exercises,
			selectedExercise,
			selectedCalories,
			selectedDuration,
		} = this.props;

		const { inputValueDuration }: CardDetailWorkoutStateTypes = this.state;
		const options = exercises
			? exercises.map((exercise) => {
					return {
						value: exercise.externalId,
						label: exercise.title,
					};
			  })
			: [];

		const selectedOption = options.find((option) => option.value === this.state.dropdownValueExercise.value);

		const currentExercise = this.getCurrentExercise();
		const calories =
			this.state.hasValidDuration && this.state.hasValidExercise
				? this.calculateCalories(
						currentExercise ? parseFloat(currentExercise.met as any) : 0,
						weight,
						parseInt(this.state.inputValueDuration, 10)
				  )
				: 0;

		const unitDurationLabel = i18n('react.impulsCoach.components.cardDetail.workout.unit.duration');
		const energyUnitLabel = i18n('react.impulsCoach.components.cardDetail.workout.energyUnitLabel');

		return (
			<div>
				<div className="m-card__content" onClick={handleCardContentClick}>
					<div className="m-card__info">
						<h4 className="m-card__info-title">
							{selectedExercise && selectedExercise.title
								? selectedExercise.title
								: i18n('react.impulsCoach.components.cardDetail.workout.custom.title')}
						</h4>
						<div className="m-card__info-line">
							<div className="m-card__info-text m-card__info-text--gap-right">
								{selectedCalories && (
									<span className="m-card__info-text-item">
										{i18n('react.impulsCoach.components.cardDetail.workout.energyLabel', {
											energy: kcal(selectedCalories),
										})}
									</span>
								)}
								{selectedDuration && (
									<span className="m-card__info-text-item">
										{selectedDuration +
											i18n('react.impulsCoach.components.cardDetail.workout.unit.duration')}
									</span>
								)}
							</div>
						</div>
					</div>
					<div className="m-card__link js-m-card__link">
						<Link
							onClick={handleShowDetailsClick}
							title={this.labelShowDetailButton}
							modifier="button"
							openInNewWindow={false}
							iconRight={null}
						/>
					</div>

					<div className="m-card__detail">
						{selectedDuration && selectedCalories && (
							<table style={{ width: '100%', marginBottom: '30px' }}>
								<tbody>
									<tr>
										<td>
											<IconInfo
												unit={energyUnitLabel}
												value={kcal(selectedCalories)}
												icon="flame"
											/>
										</td>
										<td>
											<IconInfo
												unit={unitDurationLabel}
												value={selectedDuration.toString()}
												icon="time"
											/>
										</td>
									</tr>
								</tbody>
							</table>
						)}

						<Rich contents={i18n('react.impulsCoach.components.cardDetail.workout.custom.description')} />

						{this.isCompletedNotEditing && (
							<Measurement
								modifier="editable"
								title={i18n(
									'react.impulsCoach.components.cardDetail.workout.custom.selectedExerciseTitle'
								)}
								unit={unitDurationLabel}
								measuring={{
									weight: parseInt(inputValueDuration, 10),
									date: this.state.dropdownValueExercise.label,
									time: ' ',
								}}
								onEdit={this.handleToggleEditing}
								showText={false}
							/>
						)}

						{this.isCompletedEditing && (
							<div>
								<Dropdown
									id={`exercise_${this.props.id}`}
									options={options}
									value={selectedOption ? selectedOption.value : ''}
									onChange={this.handleDropdownExercise}
									onBlur={iOSScrollKeyboardPositionFix}
									label={i18n(
										`react.impulsCoach.components.cardDetail.workout.custom.field.exercise.title`
									)}
								/>
								<ControlledInputDuration
									id={`exercise_duration_${this.props.id}`}
									value={inputValueDuration}
									onValidationDone={this.handleValidInputDuration as any}
									onChange={this.handleInputDuration as any}
									onBlur={iOSScrollKeyboardPositionFix}
									maxlength="256"
									type="text"
									suffix={this.getInputLabel('duration', 'suffix')}
									label={this.getInputLabel('duration', 'title')}
								/>
								<Text
									tag="p"
									size="xxl"
									textStyle="black"
									align="center"
									text={calories !== 0 && (`= ${kcal(calories)} kcal` as any)}
								/>
							</div>
						)}
						<Spacer size="m" />
					</div>
					{this.isCompletedEditing && this.renderButtonBar()}
					{this.iosScrollFix.renderDetailSpacer()}
				</div>
			</div>
		);
	};

	public renderWorkoutDetail = () => {
		const {
			id,
			title,
			descriptionShort,
			description,
			tableList,
			handleCardContentClick,
			handleShowDetailsClick,
			handleToggleFavorite,
			intensity,
			totalDuration,
			favorite,
			cardRenderLocation,
			parentProps,
		}: CardDetailWorkoutProps = this.props;

		const intensityLabel = i18n('react.impulsCoach.components.cardDetail.workout.intensityLabel');
		const unitDurationLabel = i18n('react.impulsCoach.components.cardDetail.workout.unit.duration');
		const energyUnitLabel = i18n('react.impulsCoach.components.cardDetail.workout.energyUnitLabel');
		const energyLabel = i18n('react.impulsCoach.components.cardDetail.workout.energyLabel', {
			energy: kcal(this.energyKcal),
		});

		return (
			<div>
				<Overlay
					preventLock
					preventUnlock
					isOpen={this.state.isOverlayOpen}
					closable
					closeButton={false}
					animation="zoom"
					onClose={this.handleOverlayClose}
				>
					{this.state.isOverlayOpened ? (
						<CardLayer
							selectedContent={
								!!~[CARD_RENDER_LOCATION.FAVORITE, CARD_RENDER_LOCATION.POOL].indexOf(
									cardRenderLocation
								)
									? parentProps
									: null
							}
							programItemId={id as any}
							selectedExerciseId={this.state.selectedExerciseId}
						/>
					) : null}
				</Overlay>
				<div className="m-card__content" onClick={handleCardContentClick}>
					<div className="m-card__info m-card__info--has-like">
						<h4 className="m-card__info-title">{title}</h4>
						<CardLike onClick={handleToggleFavorite} liked={favorite} />
						<div className="m-card__info-line">
							<div
								className={`m-card__info-text${intensity >= 0 ? ' m-card__info-text--gap-right' : ''}`}
							>
								<span className="m-card__info-text-item">{energyLabel}</span>
								<span className="m-card__info-text-item">
									{`${totalDuration} ${unitDurationLabel}`}
								</span>
								{intensity >= 0 && <span className="m-card__info-text-item">{intensityLabel}</span>}
							</div>
							<Intensity value={intensity} />
						</div>
					</div>
					<div className="m-card__link js-m-card__link">
						<Link
							onClick={handleShowDetailsClick}
							title={i18n('react.impulsCoach.components.cardDetail.workout.linkText')}
							modifier="button"
							openInNewWindow={false}
							iconRight={null}
						/>
					</div>
					<div className="m-card__detail">
						<table style={{ width: '100%', marginBottom: '30px' }}>
							<tbody>
								<tr>
									<td>
										<IconInfo unit={energyUnitLabel} value={kcal(this.energyKcal)} icon="flame" />
									</td>
									<td>
										<IconInfo
											unit={unitDurationLabel}
											value={totalDuration.toString()}
											icon="time"
										/>
									</td>
									<td>
										<Intensity label={intensityLabel} value={intensity} />
									</td>
								</tr>
							</tbody>
						</table>
						<Rich contents={descriptionShort} />
						{description ? <Rich contents={description} /> : null}
						{this.getToolLinkListData().length > 0 && (
							<React.Fragment>
								<h4 className="a-text a-text--size-l a-text--style-black">
									{i18n('react.impulsCoach.components.cardDetail.workout.toolsTitle')}
								</h4>
								<div className="m-rich">
									<p>{i18n('react.impulsCoach.components.cardDetail.workout.toolsDescription')}</p>
								</div>
								<LinkList links={this.getToolLinkListData()} />
							</React.Fragment>
						)}
						<div className="h-spacer h-spacer--m" />
						<div className="m-card__button">
							<Link
								onClick={() => this.handleTableListClick(0)}
								title={i18n('react.impulsCoach.components.cardDetail.workout.startWorkout')}
								modifier="button"
								openInNewWindow={false}
								iconRight={null}
							/>
						</div>
						<Spacer size="l" />
						<Text
							tag="h3"
							size="xl"
							textStyle="black"
							text={i18n('react.impulsCoach.components.cardDetail.workout.exercisesTitle')}
						/>
						{this.renderRepetitionsPerWorkoutSet()}
						{tableList.items && (
							<TableList items={tableList.items} handleClick={this.handleTableListClick} />
						)}
					</div>
					{this.renderButtonBar()}
				</div>
			</div>
		);
	};

	public render() {
		const { isCustom } = this.props;
		return isCustom ? this.renderCustomWorkoutDetail() : this.renderWorkoutDetail();
	}
}

export default CardDetailWorkout;
